import type { JSONContent } from "@tiptap/core";
import type { AxAttributedString, AxFullTextStyle, AxHyperlink, AxHyperlinkCanvasLink, AxHyperlinkInlineCard, AxHyperlinkMark, AxHyperlinkMention, AxInline, AxParagraph, AxRun, TextListInfo } from "../platform/attributedString";
import type { CanvasLinkAttributes } from "./links/canvas/types";
import type { InlineCardAttributes } from "./links/cards/types";
import type { LinkNodeAttributes } from "./links/types";
import type { MentionAttributes } from "./mention";
import Bold from "@tiptap/extension-bold";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import { BulletList, ListItem, OrderedList, TaskItem, TaskList } from "@tiptap/extension-list";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import Text from "@tiptap/extension-text";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { isObject, isString } from "@/common/general";
import { AxLetterCasing } from "../bridge/selection";
import { fromInlineArgb, toInlineArgb } from "../common/utils";
import {
  AxFontStyle,
  AxHyperlinkPayloadType,
  AxStringAlignment,
  Baseline,
  CURRENT_FILE_VERSION,
  FontStretch,
  getAxInline,
  getCssFontSize,
  getCssFontStretch,
  getCssFontStyle,
  getCssLetterCasing,
  getCssLineHeight,
  getCssPixelNumber,
  getCssTextAlignment,
  getFontSizeFromCss,
  getFontStretchFromCss,
  getFontStyleFromCss,
  getFontWeightFromCss,
  getLetterCasingFromCss,
  getLineSpacingFromCss,
  getNumberFromCss,
  getStringAlignment,
  InlineType,
  PX_TO_PT,
  TextListType,
} from "../platform/attributedString";
import CanvasLink from "./links/canvas/CanvasLink";
import InlineCard from "./links/cards/InlineCard";
import LinkNode from "./links/LinkNode";
import { Mention } from "./mention";

type AxTextStyle = Partial<Omit<AxRun, "Type" | "Text">>;
type AxParagraphStyle = Partial<Omit<AxParagraph, "Inline">>;
type JSONContentHeading = JSONContent & { type: "heading"; attrs: { level: number } };
type JSONContentOrderedList = JSONContent & { type: "orderedList"; attrs: { start: number } };
type JSONContentTaskItem = JSONContent & { type: "taskItem"; attrs: { checked: boolean } };

export const defaultParagraphStyle = {
  HorizontalAlignment: AxStringAlignment.Near,
  LineSpacing: 1,
  ListInfo: { IndentLevel: 0, ListType: TextListType.Bullet },
} satisfies AxParagraphStyle;

export const defaultRunStyle = {
  Family: { value: "Inter", indeterminate: false },
  Size: { value: 9.75, indeterminate: false },
  Weight: 400,
  Style: AxFontStyle.Normal,
  Stretch: FontStretch.Normal,
  Underline: false,
  Color: { value: -13421773 },
  CharacterSpacing: { value: 0, indeterminate: false },
  Strikethrough: false,
  Baseline: Baseline.Normal,
  LetterCasing: AxLetterCasing.Normal,
} satisfies AxTextStyle;

type JSONContentMarks = JSONContent["marks"];
type JSONContentMark = NonNullable<JSONContent["marks"]>[number];
interface AttributeColor {
  color: string;
}
interface AttributeFontFamily {
  fontFamily: string;
  fontFamilyReferenceId: string;
  fontFamilyReferenceName: string;
}

export interface TextStyleCssAttributes {
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  fontStyle: string;
  fontStretch: string;
  color: string;
  letterSpacing: string;
  textTransform: string;
}

export interface TextStyleReferenceAttributes {
  fontSizeReferenceId: string;
  fontSizeReferenceName: string;
  characterSpaceReferenceId: string;
  characterSpaceReferenceName: string;
  colorReferenceId: string;
  colorReferenceName: string;
  fontFamilyReferenceId: string;
  fontFamilyReferenceName: string;
  alpha: number;
}

export type TextStyleAttributes = TextStyleCssAttributes & TextStyleReferenceAttributes;

export interface CssParagraphAttributes {
  lineHeight: string;
  textAlign: string;
  computedFontSize: string | undefined;
}

const markConverterMap: Record<string, ((style: AxTextStyle, mark: JSONContentMark) => void) | undefined> = {
  [Bold.name]: s => {
    s.Weight = 700;
  },
  [Italic.name]: s => {
    s.Style = AxFontStyle.Italic;
  },
  [Underline.name]: s => {
    s.Underline = true;
  },
  [Strike.name]: s => {
    s.Strikethrough = true;
  },
  [TextStyle.name]: mapTextStyle,
} as const;

// const inlineConverterMap: Record<string, ((content: JSONContent) => AxInline) | undefined> = {
//   [Text.name]: toRun,
//   [HardBreak.name]: () => emptyRun(),
// } as const;

const newLineRun = (baseStyle: AxFullTextStyle) => toRun("\n", baseStyle);
const emptyRun = (baseStyle: AxFullTextStyle) => toRun("", baseStyle);

const contentItemConverterMap: Record<string, ((content: JSONContent, baseStyle: AxFullTextStyle) => AxParagraph | AxParagraph[]) | undefined> = {
  [Paragraph.name]: toAxParagraph,
  [Heading.name]: toParagraphHeading,
  [BulletList.name]: toList,
  [OrderedList.name]: toList,
  [TaskList.name]: toList,
} as const;

const textListTypeMap: Record<string, TextListType> = {
  [BulletList.name]: TextListType.Bullet,
  [OrderedList.name]: TextListType.Numbered,
  [TaskList.name]: TextListType.Task,
} as const;

const listConverterMap: Record<string, ((node: JSONContent, baseStyle: AxFullTextStyle, listInfo: TextListInfo, indentLevel: number) => AxParagraph[]) | undefined> = {
  [BulletList.name]: toSpecificList,
  [OrderedList.name]: toOrderedList,
  [TaskList.name]: toSpecificList,
} as const;

const listItemConverterMap: Record<string, ((content: JSONContent, baseStyle: AxFullTextStyle, listInfo: TextListInfo, indentLevel: number) => AxParagraph | AxParagraph[]) | undefined> = {
  [ListItem.name]: toListItem,
  [TaskItem.name]: toTaskListItem,
} as const;

function getLink(content: JSONContent) {
  if (!content.marks) return undefined;
  for (const mark of content.marks) {
    // const attrVal = content.attrs[attr];
    if (mark.type === Link.name) {
      return mark.attrs?.href as string | undefined;
    }
  }
  return undefined;
}

function createMentionAxHyperlink(attrs: Record<string, unknown>, baseStyle: AxFullTextStyle) {
  const mentionAttrs = attrs as unknown as MentionAttributes;
  const linkPayload: AxHyperlinkMention = {
    type: AxHyperlinkPayloadType.Mention,
    id: attrs.id as string,
    mentionId: attrs.mentionId as string,
  };
  const link: AxHyperlink = {
    Type: InlineType.AxHyperlink,
    Link: linkPayload,
    Inlines: [toRun(`@${mentionAttrs.label}`, baseStyle)],
  };
  return link;
}

function createLinkNodeAxHyperlink(attrs: Record<string, unknown>, baseStyle: AxFullTextStyle) {
  const nodeAttrs = attrs as unknown as LinkNodeAttributes;
  const linkPayload: AxHyperlinkMark = {
    type: AxHyperlinkPayloadType.Mark,
    href: nodeAttrs.url,
  };

  const run = nodeAttrs.text
    ? toRun(nodeAttrs.text, baseStyle)
    : emptyRun(baseStyle);

  const link: AxHyperlink = {
    Type: InlineType.AxHyperlink,
    Link: linkPayload,
    Inlines: [run],
  };
  return link;
}

function createInlineCardAxHyperlink(attrs: Record<string, unknown>, baseStyle: AxFullTextStyle) {
  const cardAttrs = attrs as unknown as InlineCardAttributes;
  const linkPayload: AxHyperlinkInlineCard = {
    type: AxHyperlinkPayloadType.InlineCard,
    href: cardAttrs.url,
  };
  const link: AxHyperlink = {
    Type: InlineType.AxHyperlink,
    Link: linkPayload,
    Inlines: [emptyRun(baseStyle)],
  };
  return link;
}

function createCanvasLinkAxHyperlink(attrs: Record<string, unknown>, baseStyle: AxFullTextStyle) {
  const nodeAttrs = attrs as unknown as CanvasLinkAttributes;
  const linkPayload: AxHyperlinkCanvasLink = {
    type: AxHyperlinkPayloadType.CanvasLink,
    objectId: nodeAttrs.objectId,
  };
  const link: AxHyperlink = {
    Type: InlineType.AxHyperlink,
    Link: linkPayload,
    Inlines: [emptyRun(baseStyle)],
  };
  return link;
}

// extend this dictionary to add new AxHyperlink types (like a pill box).
const JSON_TYPE_TO_AX_HYPERLINK = {
  [Mention.name]: createMentionAxHyperlink,
  [InlineCard.name]: createInlineCardAxHyperlink,
  [CanvasLink.name]: createCanvasLinkAxHyperlink,
  [LinkNode.name]: createLinkNodeAxHyperlink,
};

function toInline(content: JSONContent, baseStyle: AxFullTextStyle, currentLink?: AxHyperlink): AxInline | undefined {
  if (content.type === HardBreak.name) return newLineRun(baseStyle);
  if (content.type === Text.name) {
    const markLink = currentLink?.Link as AxHyperlinkMark;
    const link = getLink(content);
    if (link && markLink && link === markLink.href) {
      currentLink?.Inlines.push(toRun(content, baseStyle));
      return undefined;
    }
    if (link) {
      const linkPayload: AxHyperlinkMark = {
        type: AxHyperlinkPayloadType.Mark,
        href: link,
      };
      const newLink: AxHyperlink = {
        Type: InlineType.AxHyperlink,
        Link: linkPayload,
        Inlines: [toRun(content, baseStyle)],
      };
      return newLink;
    }
    return toRun(content, baseStyle);
  }
  const hyperlinkConverter = content.type ? JSON_TYPE_TO_AX_HYPERLINK[content.type] : undefined;
  if (hyperlinkConverter && content.attrs) {
    return hyperlinkConverter(content.attrs, baseStyle);
  }
  return undefined;
}

function hasColor(attributes: unknown): attributes is AttributeColor {
  return isObject(attributes) && isString(attributes.color);
}

function hasFontFamily(attributes: unknown): attributes is AttributeFontFamily {
  return isObject(attributes) && isString(attributes.fontFamily);
}

// function hasTextStyle(style: string, attributes: { [name: string]: unknown }) {
//   return style in attributes && typeof attributes[style] === "string";
// }

function isHeading(node: JSONContent): node is JSONContentHeading {
  return node.type === Heading.name;
}

function isOrderedList(node: JSONContent): node is JSONContentOrderedList {
  return node.type === OrderedList.name;
}

function isTaskItem(node: JSONContent): node is JSONContentTaskItem {
  return node.type === TaskItem.name;
}

function hexToRgb(h: string) {
  let r = "0";
  let g = "0";
  let b = "0";

  // 3 digits
  if (h.length === 4) {
    r = `0x${h[1]}${h[1]}`;
    g = `0x${h[2]}${h[2]}`;
    b = `0x${h[3]}${h[3]}`;

    // 6 digits
  } else if (h.length === 7) {
    r = `0x${h[1]}${h[2]}`;
    g = `0x${h[3]}${h[4]}`;
    b = `0x${h[5]}${h[6]}`;
  }

  return `rgb(${+r},${+g},${+b})`;
}

function convertColor(color: string): number | undefined {
  if (color.startsWith("#")) return fromInlineArgb(hexToRgb(color));
  if (color.toLowerCase().startsWith("rgb")) return fromInlineArgb(color);
  return defaultRunStyle.Color.value;
}

function mapTextStyle(style: AxTextStyle, mark: JSONContentMark, baseStyle?: AxFullTextStyle): void {
  const attrs = mark.attrs;
  if (!attrs) return;

  if (hasFontFamily(attrs)) {
    style.Family = { value: attrs.fontFamily, indeterminate: false, referenceId: attrs.fontFamilyReferenceId, referenceName: attrs.fontFamilyReferenceName };
  }

  const styleAttrs = attrs as Partial<TextStyleAttributes>;
  if (hasColor(attrs)) {
    const color = convertColor(attrs.color);
    if (!color) {
      style.Color = baseStyle?.Color;
      return;
    }
    style.Color = { value: color, referenceId: styleAttrs.colorReferenceId, referenceName: styleAttrs.colorReferenceName };
  }

  if (styleAttrs.fontSize) style.Size = { value: getFontSizeFromCss(styleAttrs.fontSize) ?? baseStyle?.Size, indeterminate: false, referenceId: styleAttrs.fontSizeReferenceId, referenceName: styleAttrs.fontSizeReferenceName };
  if (styleAttrs.fontWeight) style.Weight = getFontWeightFromCss(styleAttrs.fontWeight) ?? baseStyle?.Weight;
  if (styleAttrs.fontStyle) style.Style = getFontStyleFromCss(styleAttrs.fontStyle) ?? baseStyle?.Style;
  if (styleAttrs.fontStretch) style.Stretch = getFontStretchFromCss(styleAttrs.fontStretch) ?? baseStyle?.Stretch;
  if (styleAttrs.letterSpacing) style.CharacterSpacing = { value: getNumberFromCss(styleAttrs.letterSpacing), indeterminate: false, referenceId: styleAttrs.characterSpaceReferenceId, referenceName: styleAttrs.characterSpaceReferenceName };
  if (styleAttrs.textTransform) style.LetterCasing = getLetterCasingFromCss(styleAttrs.textTransform) ?? baseStyle?.LetterCasing;
}

function marksToRunStyle(marks: JSONContentMarks | undefined, baseStyle: AxFullTextStyle): Required<AxTextStyle> {
  if (!marks) return { ...baseStyle };

  // we must initialize DISABLED values of Underline and Strikethrough. Marks can only ENABLE this props. PX-1263
  const textStyle: AxTextStyle = {
    Underline: false,
    Strikethrough: false,
  };

  for (const mark of marks) {
    const markType = mark.type;
    const styleSetter = markConverterMap[markType];
    if (styleSetter) styleSetter(textStyle, mark);
  }

  return {
    ...baseStyle,
    ...textStyle,
  };
}

function toRun(textNode: JSONContent | string, baseStyle: AxFullTextStyle): AxRun {
  if (isString(textNode)) {
    return getAxInline(baseStyle ?? defaultRunStyle, textNode);
  }
  return {
    Type: InlineType.AxRun,
    Text: textNode.text ?? "\n",
    ...marksToRunStyle(textNode.marks, baseStyle),
  };
}

function emptyParagraph(baseStyle: AxFullTextStyle): AxParagraph {
  const par = {
    Inlines: [newLineRun(baseStyle)],
    ...defaultParagraphStyle,
  } as AxParagraph;
  if (baseStyle) {
    par.HorizontalAlignment = baseStyle.HorizontalAlignment;
    par.LineSpacing = baseStyle.LineSpacing;

    // we should ignore list info here to avoid unnecessary lists. PX-1310 PX-1203
    // par.ListInfo = baseStyle.ListInfo;
  }
  return par;
}

const headingToSizeMap: Record<number, number | undefined> = {
  1: 32 * PX_TO_PT,
  2: 24 * PX_TO_PT,
  3: 18 * PX_TO_PT,
  4: 14 * PX_TO_PT,
  5: 13 * PX_TO_PT,
  6: 10 * PX_TO_PT,
};

function toList(node: JSONContent, baseStyle: AxFullTextStyle, indentLevel = 1) {
  if (!node.type || !isListNode(node)) return [];

  const listInfo = {
    ListType: textListTypeMap[node.type] || TextListType.Bullet,
    IndentLevel: indentLevel,
  };

  const converter = listConverterMap[node.type];
  if (!converter) return [];

  return converter(node, baseStyle, listInfo, indentLevel);
}

function isListNode(node: JSONContent) {
  return node.type === BulletList.name || node.type === OrderedList.name || node.type === TaskList.name;
}

function toListItem(node: JSONContent, baseStyle: AxFullTextStyle, listInfo: TextListInfo, indentLevel = 1) {
  const content = node.content;
  if (!content) return [];

  const items = [];
  for (const listContent of content) {
    const contentType = listContent.type;
    if (!contentType) continue;

    if (isListNode(listContent)) {
      const nestedList = toList(listContent, baseStyle, indentLevel + 1);
      items.push(...nestedList);
      continue;
    }

    const converter = contentItemConverterMap[contentType];
    if (converter) {
      const converted = converter(listContent, baseStyle);
      if (Array.isArray(converted)) {
        converted.forEach(p => {
          p.ListInfo = listInfo;
        });
        items.push(...converted);
      } else {
        converted.ListInfo = listInfo;
        items.push(converted);
      }
    }
  }
  return items;
}

function toTaskListItem(node: JSONContent, baseStyle: AxFullTextStyle, listInfo: TextListInfo, indentLevel = 1) {
  if (!isTaskItem(node)) return toListItem(node, baseStyle, listInfo, indentLevel);

  const checked = node.attrs.checked;
  const taskListInfo = {
    ...listInfo,
    Checked: checked,
  };
  return toListItem(node, baseStyle, taskListInfo, indentLevel);
}

function toOrderedList(node: JSONContent, baseStyle: AxFullTextStyle, listInfo: TextListInfo, indentLevel = 1) {
  if (!isOrderedList(node)) return toSpecificList(node, baseStyle, listInfo, indentLevel);
  const orderedListInfo = {
    ...listInfo,
    Start: node.attrs.start,
  };
  return toSpecificList(node, baseStyle, orderedListInfo, indentLevel);
}

function toSpecificList(node: JSONContent, baseStyle: AxFullTextStyle, listInfo: TextListInfo, indentLevel = 1): AxParagraph[] {
  if (!node.content) return [];

  const listParagraphs = [];
  const listItems = node.content;

  // handle computedFontSize of list
  const listBaseStyle: Partial<AxFullTextStyle> = {};
  const listFontSize = node.attrs?.computedFontSize;
  if (listFontSize) {
    listBaseStyle.Size = { value: getFontSizeFromCss(listFontSize), indeterminate: false };
  }

  for (const listItem of listItems) {
    const listItemContent = listItem.content;

    // handle computedFontSize of list item
    const listItemBaseStyle: Partial<AxFullTextStyle> = {};
    const listItemFontSize = listItem.attrs?.computedFontSize;
    if (listItemFontSize) {
      listItemBaseStyle.Size = { value: getFontSizeFromCss(listItemFontSize), indeterminate: false };
    }

    // we have to combine styles to get overridden props
    const combinedBaseStyle = { ...baseStyle, ...listBaseStyle, ...listItemBaseStyle };

    if (!listItemContent) {
      const paragraph = emptyParagraph(combinedBaseStyle);
      paragraph.ListInfo = listInfo;
      listParagraphs.push(paragraph);
      continue;
    }

    const listItemType = listItem.type;
    if (!listItemType) continue;

    const listItemConverter = listItemConverterMap[listItemType];
    if (listItemConverter) {
      const converted = listItemConverter(listItem, combinedBaseStyle, listInfo, indentLevel);
      if (Array.isArray(converted)) {
        listParagraphs.push(...converted);
      } else {
        listParagraphs.push(converted);
      }
    }
  }

  return listParagraphs;
}

function applyToAllRuns(inlines: AxInline[], fn: ((run: AxRun) => void)) {
  for (const inline of inlines) {
    if (inline.Type === InlineType.AxHyperlink) {
      for (const run of inline.Inlines) fn(run);
    } else {
      fn(inline);
    }
  }
}

function toParagraphHeading(node: JSONContent, baseStyle: AxFullTextStyle): AxParagraph | AxParagraph[] {
  const paragraph = toAxParagraph(node, baseStyle);

  if (!isHeading(node)) {
    return paragraph;
  }

  const level = node.attrs.level;
  const fontSize = headingToSizeMap[level];
  if (!fontSize) return paragraph;
  if (Array.isArray(paragraph)) {
    for (const p of paragraph) {
      applyToAllRuns(p.Inlines, run => {
        run.Size = { value: fontSize, indeterminate: false };
      });
    }
  } else {
    applyToAllRuns(paragraph.Inlines, run => {
      run.Size = { value: fontSize, indeterminate: false };
    });
  }

  return paragraph;
}

function toAxParagraph(node: JSONContent, baseStyle: AxFullTextStyle): AxParagraph | AxParagraph[] {
  // handle computedFontSize of paragraph
  const paragraphBaseStyle: Partial<AxFullTextStyle> = {};
  const computedFontSize = node.attrs?.computedFontSize;
  if (computedFontSize) {
    paragraphBaseStyle.Size = { value: getFontSizeFromCss(computedFontSize), indeterminate: false };
  }
  // we have to combine styles to get overridden props
  const combinedBaseStyle = { ...baseStyle, ...paragraphBaseStyle };

  if (!node.content) {
    // when no content return empty paragraph
    // i am using base style, here, but theoretically you could sniff out the existing css style on the
    // item. I'll leave that as an improvement for later.
    return emptyParagraph(combinedBaseStyle);
  }

  const inlinesGroups: AxInline[][] = [[]];
  let currentLink: AxHyperlink | undefined;
  for (const content of node.content) {
    const currentGroup = inlinesGroups[inlinesGroups.length - 1];
    const nextInline = toInline(content, combinedBaseStyle, currentLink);
    if (nextInline) {
      if (content.type === HardBreak.name) {
        inlinesGroups.push([]);
      } else {
        currentGroup.push(nextInline);
      }
      currentLink = nextInline.Type === InlineType.AxHyperlink ? nextInline : undefined;
    }
  }

  const parStyles: AxParagraphStyle = {};
  const parAttrs = node.attrs as Partial<CssParagraphAttributes>;
  if (parAttrs) {
    // here we need to treat par attributes special because it has a default value and won't be null.
    if (parAttrs.lineHeight) {
      const val = getLineSpacingFromCss(parAttrs.lineHeight) ?? combinedBaseStyle.LineSpacing;
      if (val) parStyles.LineSpacing = val;
    }
    if (parAttrs.textAlign) {
      const val = getStringAlignment(parAttrs.textAlign) ?? combinedBaseStyle.HorizontalAlignment;
      if (val) parStyles.HorizontalAlignment = val;
    }
  }

  const pars = inlinesGroups.map(inlines => {
    if (!inlines.length) return emptyParagraph(combinedBaseStyle);

    const paragraph: AxParagraph = {
      Inlines: inlines,
      ...defaultParagraphStyle,
      ...parStyles,
    };
    return paragraph;
  });
  if (pars.length === 1) return pars[0];
  return pars;
}

function documentToParagraphs(root: JSONContent, baseStyle: AxFullTextStyle): AxParagraph[] {
  const document = root;
  if (document.type !== Document.name
    || !document.content
    || document.content.length === 0) {
    return [emptyParagraph(baseStyle)];
  }

  const paragraphs: AxParagraph[] = [];

  for (const content of document.content) {
    const contentType = content.type;
    if (contentType) {
      const converter = contentItemConverterMap[contentType];
      if (converter) {
        const converted = converter(content, baseStyle);
        if (Array.isArray(converted)) {
          paragraphs.push(...converted);
        } else {
          paragraphs.push(converted);
        }
      }
    }
  }

  return paragraphs;
}

export function jsonContentToAttributedString(root: JSONContent, baseStyle: AxFullTextStyle): AxAttributedString {
  const paragraphs = documentToParagraphs(root, baseStyle);

  return {
    Version: CURRENT_FILE_VERSION,
    Paragraphs: paragraphs,
  };
}

// BELOW HERE IS AxAttributedString -> JSONContent

function addBooleanMark(marks: JSONContentMark[], val: boolean, markName: string) {
  if (val) {
    marks.push({
      type: markName,
    });
  }
}

function getRunMarks(run: AxRun): JSONContentMark[] {
  const val = [
    {
      type: TextStyle.name,
      attrs: {
        fontFamily: run.Family.value,
        fontSize: getCssFontSize(run.Size.value),
        fontWeight: run.Weight.toString(),
        fontStyle: getCssFontStyle(run.Style),
        fontStretch: getCssFontStretch(run.Stretch),
        color: toInlineArgb(run.Color.value),
        letterSpacing: getCssPixelNumber(run.CharacterSpacing.value),
        textTransform: getCssLetterCasing(run.LetterCasing),
        fontSizeReferenceId: run.Size.referenceId,
        fontSizeReferenceName: run.Size.referenceName,
        colorReferenceId: run.Color.referenceId,
        colorReferenceName: run.Color.referenceName,
        fontFamilyReferenceId: run.Family.referenceId,
        fontFamilyReferenceName: run.Family.referenceName,
        characterSpaceReferenceId: run.CharacterSpacing.referenceId,
        characterSpaceReferenceName: run.CharacterSpacing.referenceName,
      },
    },
  ];
  addBooleanMark(val, run.Underline, Underline.name);
  addBooleanMark(val, run.Strikethrough, Strike.name);

  return val;
}

function createJsonMention(payload: AxHyperlinkMention, content: string) {
  if (!payload) return undefined;
  return {
    type: Mention.name,
    attrs: {
      id: payload.id,
      label: content.replace("@", ""),
      mentionId: payload.mentionId,
    },
  };
}

function createJsonInlineCard(payload: AxHyperlinkInlineCard, content: string) {
  if (!payload) return undefined;

  const attrs: InlineCardAttributes = {
    url: payload.href,
    text: content,
  };

  return {
    type: InlineCard.name,
    attrs,
  };
}

function createJsonCanvasLink(payload: AxHyperlinkCanvasLink) {
  if (!payload) return undefined;
  const attrs: CanvasLinkAttributes = {
    objectId: payload.objectId,
  };
  return {
    type: CanvasLink.name,
    attrs,
  };
}

function createJsonLink(payload: AxHyperlinkMark, inlines: AxRun[]) {
  // links are special because they have inline fomatted text and currently this is the only inline block
  // that we plan to support formatting inside of. If this changes, we will need to revisit this.
  const attrs: LinkNodeAttributes = {
    url: payload.href,
    text: inlines.map(i => i.Text).join(),
  };
  const linkNode = {
    type: LinkNode.name,
    attrs,
  };
  // const content: JSONContent[] = [];
  // for (const run of inlines) {
  //   // for now these inlines must all be runs
  //   const marks = getRunMarks(run);
  //   marks.push({ ...linkMark });
  //   content.push({
  //     type: Text.name,
  //     text: run.Text,
  //     marks,
  //   });
  // }
  return linkNode;
}

function getJsonInlines(inline: AxInline): JSONContent | JSONContent[] | undefined {
  if (inline.Type === InlineType.AxHyperlink) {
    const payload = inline.Link;
    const textInline = inline.Inlines[0];
    if (!textInline) return undefined;

    // when we want to support more inline types (like the pillbox), add the creator to this 'if' statement.
    if (payload.type === AxHyperlinkPayloadType.Mark) {
      return createJsonLink(payload, inline.Inlines);
    } else if (payload.type === AxHyperlinkPayloadType.Mention) {
      return createJsonMention(payload, textInline.Text);
    } else if (payload.type === AxHyperlinkPayloadType.InlineCard) {
      return createJsonInlineCard(payload, textInline.Text);
    } else if (payload.type === AxHyperlinkPayloadType.CanvasLink) {
      return createJsonCanvasLink(payload);
    }
    return undefined;
  }
  if (inline.Type === InlineType.AxRun && inline.Text !== "\n" && inline.Text !== "") {
    return {
      type: Text.name,
      text: inline.Text,
      marks: getRunMarks(inline),
    };
  }
  return undefined;
}

function getParagraphAttributes(par: AxParagraph) {
  const paragraphAtributes = {
    textAlign: getCssTextAlignment(par.HorizontalAlignment),
    lineHeight: getCssLineHeight(par.LineSpacing),
  };

  if (par.Inlines.length >= 1 && par.Inlines[0].Type === InlineType.AxRun) {
    return { ...paragraphAtributes, computedFontSize: getCssFontSize(par.Inlines[0].Size.value) };
  }

  return paragraphAtributes;
}

function getListType(type: TextListType) {
  if (type === TextListType.Task) return TaskList.name;
  if (type === TextListType.Numbered) return OrderedList.name;
  return BulletList.name;
}

function getListItemName(listType: TextListType) {
  if (listType === TextListType.Task) return TaskItem.name;
  return ListItem.name;
}

interface JsonParagraph {
  type: string;
  content: JSONContent[];
  attrs: {
    textAlign: string;
    lineHeight: string;
    computedFontSize?: string;
  };
}

interface JsonListItemAttrs {
  checked?: boolean;
  computedFontSize?: string;
}

interface JsonListItem {
  type: string;
  attrs?: JsonListItemAttrs;
  content: (JsonParagraph | JsonList)[];
}

interface JsonList {
  type: string;
  content: JsonListItem[];
  attrs?: JsonListAttrs;
}

interface JsonListAttrs {
  start?: number;
  computedFontSize?: string;
}

function getJsonParagraph(par: AxParagraph): JsonParagraph {
  const content: JSONContent[] = [];
  for (const inline of par.Inlines) {
    const inlineContent = getJsonInlines(inline);
    if (Array.isArray(inlineContent)) {
      content.push(...inlineContent);
    } else if (inlineContent) {
      content.push(inlineContent);
    }
  }
  return {
    type: Paragraph.name,
    content,
    attrs: getParagraphAttributes(par),
  };
  // if (par.ListInfo.IndentLevel === currentIndent) {
  //   return {
  //     type: ListItem.name,
  //     content: {
  //       type: Paragraph.name,
  //       content: par.Inlines.map(getJsonInlines),
  //       attrs: getParagraphAttributes(par),
  //     },
  //   };
  // }
  // else {
  //   return {
  //     name:getListType()
  //   }
  // }
}

function closeLists(currentLists: JsonList[], docContent: (JsonParagraph | JsonList)[], par?: AxParagraph) {
  while (currentLists.length > (par?.ListInfo?.IndentLevel ?? 0)) {
    const list = currentLists.pop()!;
    if (currentLists.length > 0) {
      const lastList = currentLists[currentLists.length - 1]!;
      const lastListItem = lastList?.content[lastList.content.length - 1];
      if (lastListItem) {
        lastListItem.content.push(list);
      }
    } else {
      docContent.push(list);
    }
  }
  if (par?.ListInfo && currentLists.length && getListType(par?.ListInfo?.ListType) !== currentLists[currentLists.length - 1]?.type) {
    docContent.push(currentLists.pop()!);
  }
}

export function attributedStringToJsonContent(root: AxAttributedString) {
  const docContent: (JsonParagraph | JsonList)[] = [];
  const currentLists: JsonList[] = [];
  // const lastListIndex = 0;
  // const lastListType = TextListType.Bullet;

  for (const par of root.Paragraphs) {
    const parContent = getJsonParagraph(par);
    const computedFontSize = parContent.attrs?.computedFontSize;

    closeLists(currentLists, docContent, par);
    if (par.ListInfo.IndentLevel === 0) {
      docContent.push(parContent);
    } else if (par.ListInfo.IndentLevel === currentLists.length) {
      currentLists[currentLists.length - 1]?.content.push({
        type: getListItemName(par.ListInfo.ListType),
        attrs: {
          checked: par.ListInfo.Checked,
        },
        content: [parContent],
      });
    } else if (par.ListInfo.IndentLevel > currentLists.length) {
      const newList = {
        type: getListType(par.ListInfo.ListType),
        content: [{
          type: getListItemName(par.ListInfo.ListType),
          attrs: {
            checked: par.ListInfo.Checked,
          },
          content: [parContent],
        }],
        attrs: {
          start: par.ListInfo.Start,
          computedFontSize,
        },
      };
      currentLists.push(newList);
    }
  }
  closeLists(currentLists, docContent);

  return {
    type: Document.name,
    // content: root.Paragraphs.map(getJsonParagraph),
    content: docContent,
  };
}

// TEXT Converstions

export function jsonContentToText(root: JSONContent) {
  const text = [] as string[];
  let isFirst = true;
  root.content?.forEach(par => {
    if (!isFirst) {
      text.push("\n");
      isFirst = false;
    }
    par.content?.forEach(run => {
      if (run.text) text.push(run.text);
    });
  });
  return text.join("");
}
